import {
  handleSubmitMultilingualForm,
  useCreateExpertise,
  useGetExpertiseFormData,
  useUpdateExpertise,
} from "@/api/cms";
import ExpertiseForm from "@/components/cms/ExpertiseForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageSchema, expertiseContentSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const expertiseFormSchema = multilingualFieldSchema(
  cmsPageSchema
    .pick({
      published: true,
      pageName: true,
    })
    .merge(
      expertiseContentSchema.omit({
        id: true,
      }),
    )
    .optional(),
);

type ExpertiseFormData = z.infer<typeof expertiseFormSchema>;

const ExpertiseCMS = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const create = useCreateExpertise();
  const update = useUpdateExpertise();

  const { data: expertiseData } = useGetExpertiseFormData();

  const defaultValues: ExpertiseFormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const exp = expertiseData?.[lang];
        return {
          published: exp?.published ?? false,
          pageName: exp?.pageName ?? "",
          title: exp?.content.title ?? "",
          content: exp?.content.content ?? "",
          formDownloadCTA: exp?.content.formDownloadCTA ?? "",
          documentUrl: exp?.content.documentUrl ?? "",
        };
      }),
    [expertiseData],
  );

  const form = useForm<ExpertiseFormData>({
    resolver: zodResolver(expertiseFormSchema),
    values: defaultValues,
  });

  const { reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const { toast } = useToast();

  const onSubmit = (submittedData: ExpertiseFormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = expertiseData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName: localeData.pageName,
            published: localeData.published,
            content: {
              title: localeData.title,
              content: localeData.content,
              formDownloadCTA: localeData.formDownloadCTA,
              documentUrl: localeData.documentUrl,
            },
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout pageName={t("cmsTabExpertise")} formIds={["expertise"]}>
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmitMultilingualForm(form, expertiseData, onSubmit)}
          className="flex w-full gap-4"
          id="expertise"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => <ExpertiseForm language={lang} />}
          />
        </form>
      </FormProvider>
    </CMSLayout>
  );
};

export default ExpertiseCMS;
